import React, { useEffect, useState } from 'react';
import { User } from 'lucide-react';
import { getAssetUrl } from '../api/client';
import { cn } from '../lib/utils';

export interface AvatarPerson {
  id?: string;
  login: string;
  name: string | null;
  avatarUrl?: string | null;
}

interface UserAvatarProps {
  person: AvatarPerson;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClass = {
  sm: 'h-8 w-8 text-[10px]',
  md: 'h-10 w-10 text-xs',
  lg: 'h-20 w-20 text-2xl',
};

function initialsFor(person: AvatarPerson): string {
  const label = (person.name || person.login || '').trim();
  if (!label) return '';
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return label.slice(0, 2).toUpperCase();
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ person, size = 'md', className }) => {
  const [failed, setFailed] = useState(false);
  const src = getAssetUrl(person.avatarUrl);
  const initials = initialsFor(person);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-foreground bg-card font-black text-foreground shadow-neo-sm',
        sizeClass[size],
        className
      )}
      title={person.name || person.login}
      aria-label={person.name || person.login || 'User avatar'}
    >
      {src && !failed ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : initials ? (
        <span>{initials}</span>
      ) : (
        <User className={size === 'lg' ? 'h-8 w-8' : 'h-4 w-4'} aria-hidden="true" />
      )}
    </div>
  );
};

interface AvatarStackProps {
  people: AvatarPerson[];
  max?: number;
  size?: 'sm' | 'md';
  className?: string;
}

export const AvatarStack: React.FC<AvatarStackProps> = ({
  people,
  max = 4,
  size = 'sm',
  className,
}) => {
  if (people.length === 0) return null;

  const visiblePeople = people.slice(0, max);
  const extra = Math.max(0, people.length - visiblePeople.length);

  return (
    <div className={cn('flex items-center -space-x-2', className)}>
      {visiblePeople.map((person, index) => (
        <UserAvatar
          key={person.id || `${person.login}-${index}`}
          person={person}
          size={size}
          className="bg-card"
        />
      ))}
      {extra > 0 && (
        <div className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-foreground bg-primary text-[10px] font-black shadow-neo-sm">
          +{extra}
        </div>
      )}
    </div>
  );
};
